import React, { useContext } from "react";
import { useSearchParams } from "react-router-dom";
import './css/ShopCategory.css';
import { ShopContext } from "../context/ShopContext";
import Item from '../components/items/Item';
import CustomizeBlouseItem from '../components/items/CustomizeBlouseItem';
import CategoryTabs from '../components/categorytabs/CategoryTabs';
import TailorServiceGrid from '../components/categorytabs/TailorServiceGrid';
import ScrollToTop from "../components/util/ScrollToTop";
import Seo from "../components/seo/Seo";

const CATEGORY_SEO = {
    cake: {
        title: "Custom Cakes",
        description: "Custom cakes in every flavor - chocolate, fusion, step cakes, and themed designs. Egg or eggless, pickup or delivery in East Brunswick, NJ.",
        path: "/cake",
    },
    tailoring: {
        title: "Tailoring & Aari Work",
        description: "Aari work, blouse stitching, alterations, saree services, and lehenga tailoring in East Brunswick, NJ.",
        path: "/tailor",
    },
    sweet: {
        title: "Sweets",
        description: "Traditional and fusion sweets, made fresh in East Brunswick, NJ.",
        path: "/sweet",
    },
    "semi-sweet": {
        title: "Semi-Sweet Treats",
        description: "Semi-sweet snacks and treats, made fresh in East Brunswick, NJ.",
        path: "/semi-sweet",
    },
    snack: {
        title: "Snacks",
        description: "Savory snacks made fresh in East Brunswick, NJ.",
        path: "/snack",
    },
};

// Each tailoring tab is its own indexable page (listed in public/sitemap.xml),
// so it gets its own title/description and a self-referencing canonical -
// otherwise every tab would canonicalize to /tailor and Google would skip it.
// Tabs missing here ("all", "popular") fall back to the category-level SEO.
const TAB_SEO = {
    cake: {
        "fusion": {
            title: "Fusion Cakes - Rasmalai, Gulab Jamun & Guava",
            description: "Indian fusion cakes - rasmalai, gulab jamun, rasagulla, guava, butterscotch, and more. Egg or eggless, pickup or delivery in East Brunswick, NJ.",
        },
        "chocolate": {
            title: "Chocolate Cakes",
            description: "Chocolate truffle, chocolate fudge, Oreo, and white forest cakes. Egg or eggless, pickup or delivery in East Brunswick, NJ.",
        },
        "step-cake": {
            title: "Step Cakes",
            description: "Multi-tier step cakes for birthdays, graduations, and celebrations. Egg or eggless, pickup or delivery in East Brunswick, NJ.",
        },
        "themed": {
            title: "Themed & Custom Cakes",
            description: "Custom themed cakes for birthdays and parties - car, football, and your own ideas. Egg or eggless, pickup or delivery in East Brunswick, NJ.",
        },
    },
    tailoring: {
        "aari": {
            title: "Aari Work Blouses",
            description: "Hand-done aari work blouses - peacock, floral, paisley, and gold-trim designs in East Brunswick, NJ.",
        },
        "blouse": {
            title: "Blouse Stitching",
            description: "Custom blouse stitching - pick a ready design or design your own front and back. Tailored to your measurements in East Brunswick, NJ.",
        },
        "alteration": {
            title: "Clothing Alterations",
            description: "Ladies' and kids' clothing alterations for a better fit in East Brunswick, NJ.",
        },
        "half-saree": {
            title: "Half Saree Stitching",
            description: "Half saree (pavadai thavani) stitching, tailored to your measurements in East Brunswick, NJ.",
        },
        "saree-threading": {
            title: "Saree Knot",
            description: "Saree knot work to finish your saree pallu, done by hand in East Brunswick, NJ.",
        },
        "saree-falls": {
            title: "Saree Falls",
            description: "Saree falls stitching, ready to drape, in East Brunswick, NJ.",
        },
        "kids": {
            title: "Kids Dress Stitching",
            description: "Custom girls' top and skirt stitching for kids in East Brunswick, NJ.",
        },
        "lehenga": {
            title: "Lehenga Stitching",
            description: "Custom lehenga stitching, tailored to your measurements in East Brunswick, NJ.",
        },
        "art-handicraft": {
            title: "Thanjavur Painting",
            description: "Handmade Thanjavur (Tanjore) paintings - peacock, Radha & Krishna, medallion, and more in East Brunswick, NJ.",
        },
    },
};

// "All Flavors" for cakes shows one card per unique flavor (e.g. one Guava, one
// Butterscotch) rather than every design variant - the popular variant wins as
// the representative card when a flavor has one, otherwise the first found.
const dedupeByFlavor = (products) => {
    const sorted = [...products].sort((a, b) => Boolean(b.popular) - Boolean(a.popular));
    const seen = new Set();
    return sorted.filter((item) => {
        const flavor = item.name.split(" - ")[0];
        if (seen.has(flavor)) return false;
        seen.add(flavor);
        return true;
    });
};

const ShopCategory = (props) => {
    const { all_product } = useContext(ShopContext);
    // The active tab lives in the URL (not just component state) so browser
    // back/forward restores the exact tab you were on, instead of resetting
    // to the default whenever you return from a product page.
    const [searchParams, setSearchParams] = useSearchParams();
    const activeSubCategory = searchParams.get("tab") || "all";
    const setActiveSubCategory = (tab) => setSearchParams({ tab });

    const categoryProducts = all_product.filter((item) => item.category === props.category);
    // "all" is a computed aggregate for cake (one card per flavor) but a real
    // subCategory value for tailoring (items sourced from tailor/all-services/), so it
    // falls through to the normal subCategory filter for every other category.
    const visibleProducts = activeSubCategory === "popular"
        ? categoryProducts.filter((item) => item.categoryPopular)
        : activeSubCategory === "all" && props.category === "cake"
        ? dedupeByFlavor(categoryProducts)
        : categoryProducts.filter((item) => (item.menu ?? item.subCategory) === activeSubCategory);

    const tabSeo = TAB_SEO[props.category]?.[activeSubCategory];
    const categorySeo = CATEGORY_SEO[props.category];
    const seo = tabSeo && categorySeo
        ? { ...tabSeo, path: `${categorySeo.path}?tab=${activeSubCategory}` }
        : categorySeo;

    return (
        <>
            {seo && <Seo title={seo.title} description={seo.description} path={seo.path} />}
            <ScrollToTop />
            <div className="shop-category">
                <CategoryTabs category={props.category} activeTab={activeSubCategory} onSelect={setActiveSubCategory} />

                <div className="shopcategory-products">
                    {activeSubCategory === "all" && props.category === "tailoring" ? (
                        <TailorServiceGrid onSelect={setActiveSubCategory} />
                    ) : (
                        <div className="shopcategory-items">
                            {activeSubCategory === "blouse" && props.category === "tailoring" && <CustomizeBlouseItem />}
                            {visibleProducts.map((item) => (
                                <Item key={item.id} product={item} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default ShopCategory;
